import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export type FolderSource = 'MANUAL' | 'ONEDRIVE' | 'GOOGLE_DRIVE' | 'DROPBOX';

export interface Folder {
  _id: string;
  id?: string;
  document_store_id: string;
  parent_folder_id: string | null;
  name: string;
  source: FolderSource;
  created_by: string;
  is_deleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Returned by /folder/utils/list (direct children). Not present on
  // every endpoint; treat as optional.
  subfolder_count?: number;
  file_count?: number;
}

export interface CreateFolderParams {
  document_store_id: string;
  parent_folder_id?: string | null;
  name: string;
  source?: FolderSource;
}

export interface RenameFolderParams {
  name: string;
}

export interface MoveFolderParams {
  new_parent_folder_id?: string | null;
}

export interface ListFoldersParams {
  document_store_id: string;
  parent_folder_id?: string | null;
}

export interface DeleteFolderResponse {
  message: string;
  deleted_folder_ids: string[];
  deleted_file_ids: string[];
}

class FoldersService {
  async createFolder(params: CreateFolderParams): Promise<Folder> {
    const response = await apiClient.post(API_ENDPOINTS.FOLDERS.CREATE, params);
    return response.data?.data ?? response.data;
  }

  async getFolder(id: string): Promise<Folder> {
    const response = await apiClient.get(API_ENDPOINTS.FOLDERS.GET_BY_ID(id));
    return response.data?.data ?? response.data;
  }

  async listChildren(params: ListFoldersParams): Promise<Folder[]> {
    const response = await apiClient.post(API_ENDPOINTS.FOLDERS.LIST_CHILDREN, {
      document_store_id: params.document_store_id,
      parent_folder_id: params.parent_folder_id ?? null,
    });
    return response.data?.data ?? response.data ?? [];
  }

  async listAll(documentStoreId: string): Promise<Folder[]> {
    const response = await apiClient.post(API_ENDPOINTS.FOLDERS.LIST_ALL, {
      document_store_id: documentStoreId,
    });
    return response.data?.data ?? response.data ?? [];
  }

  async renameFolder(id: string, params: RenameFolderParams): Promise<Folder> {
    const response = await apiClient.put(API_ENDPOINTS.FOLDERS.UPDATE(id), params);
    return response.data?.data ?? response.data;
  }

  async moveFolder(id: string, params: MoveFolderParams): Promise<Folder> {
    const response = await apiClient.put(API_ENDPOINTS.FOLDERS.MOVE(id), {
      new_parent_folder_id: params.new_parent_folder_id ?? null,
    });
    return response.data?.data ?? response.data;
  }

  async deleteFolder(id: string): Promise<DeleteFolderResponse> {
    const response = await apiClient.delete(API_ENDPOINTS.FOLDERS.DELETE(id));
    return response.data?.data ?? response.data;
  }
}

export const foldersService = new FoldersService();
export default foldersService;
